import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Star, Instagram, Facebook, Youtube, Linkedin, Twitter } from "lucide-react";
import { WhatsAppIcon } from "@/components/site/BrandIcons";

export default function Contact() {
  const [copy, setCopy] = useState({});
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/homepage").then((r) => setCopy(r.data || {})); }, []);
  const c = copy.contact || {};
  const s = c.socials || {};

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent — we'll get back to you shortly");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) { toast.error(apiError(err)); }
    finally { setLoading(false); }
  };

  const socials = [
    { key: "instagram", url: s.instagram, Icon: Instagram },
    { key: "facebook", url: s.facebook, Icon: Facebook },
    { key: "youtube", url: s.youtube, Icon: Youtube },
    { key: "twitter", url: s.twitter, Icon: Twitter },
    { key: "linkedin", url: s.linkedin, Icon: Linkedin },
  ].filter((x) => x.url);

  const whatsappHref = s.whatsapp
    ? (s.whatsapp.startsWith("http") ? s.whatsapp : `https://wa.me/${s.whatsapp.replace(/[^0-9]/g, "")}`)
    : null;

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-16" data-testid="contact-page">
      <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-4">// Get in Touch</div>
      <h1 className="font-display text-5xl md:text-7xl font-black uppercase leading-none tracking-tighter">
        Come <span className="brand-blue">visit us</span>.
      </h1>
      <p className="text-white/60 mt-6 max-w-2xl">
        Drop by, call, message us — or leave a Google review. We&apos;re on the map.
      </p>

      {whatsappHref && (
        <a
          href={whatsappHref}
          target="_blank"
          rel="noreferrer"
          data-testid="contact-whatsapp-cta"
          className="mt-8 inline-flex items-center gap-3 bg-[#25D366] text-black px-6 py-4 font-bold text-sm uppercase tracking-widest hover:opacity-90"
        >
          <WhatsAppIcon size={18} /> Chat with us on WhatsApp
        </a>
      )}

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left: form + info */}
        <div>
          <div className="border border-white/12 bg-[#131A2B] p-6 mb-6">
            <div className="font-mono text-xs uppercase tracking-widest brand-blue mb-4">// Reach out</div>
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-3"><MapPin size={16} className="brand-orange mt-1 shrink-0" /><span>{c.address || "—"}</span></div>
              <a href={`tel:${c.phone || ""}`} className="flex items-center gap-3 hover:brand-blue"><Phone size={16} className="brand-orange" /> {c.phone || "—"}</a>
              <a href={`mailto:${c.email || ""}`} className="flex items-center gap-3 hover:brand-blue"><Mail size={16} className="brand-orange" /> {c.email || "—"}</a>
            </div>

            {socials.length > 0 && (
              <div className="mt-6 pt-6 border-t border-white/12">
                <div className="font-mono text-xs uppercase tracking-widest text-white/40 mb-3">Follow us</div>
                <div className="flex gap-3">
                  {socials.map(({ key, url, Icon }) => (
                    <a key={key} href={url} target="_blank" rel="noreferrer" data-testid={`social-${key}`} className="w-10 h-10 border border-white/20 hover:border-brand-blue hover:text-brand-blue flex items-center justify-center">
                      <Icon size={16} />
                    </a>
                  ))}
                </div>
              </div>
            )}

            {c.google_business_url && (
              <a href={c.google_business_url} target="_blank" rel="noreferrer" data-testid="google-review-cta" className="mt-6 flex items-center justify-center gap-2 bg-brand-orange text-white px-5 py-3 font-bold text-sm uppercase tracking-widest hover:glow-orange">
                <Star size={14} /> Leave us a Google Review
              </a>
            )}
          </div>

          <form onSubmit={submit} className="border border-white/12 bg-[#131A2B] p-6 space-y-3" data-testid="contact-form">
            <div className="font-mono text-xs uppercase tracking-widest brand-blue mb-2">// Send a message</div>
            <div className="grid grid-cols-2 gap-3">
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" data-testid="contact-name" className="bg-transparent border border-white/20 py-3 px-4 col-span-2 md:col-span-1" />
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" data-testid="contact-email" className="bg-transparent border border-white/20 py-3 px-4 col-span-2 md:col-span-1" />
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone (optional)" data-testid="contact-phone" className="bg-transparent border border-white/20 py-3 px-4" />
              <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Subject" data-testid="contact-subject" className="bg-transparent border border-white/20 py-3 px-4" />
              <textarea required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Message" rows={5} data-testid="contact-message" className="bg-transparent border border-white/20 py-3 px-4 col-span-2" />
            </div>
            <button type="submit" disabled={loading} data-testid="contact-submit" className="w-full bg-brand-blue text-white py-3 font-bold uppercase tracking-widest hover:glow-blue disabled:opacity-50">
              {loading ? "Sending…" : "Send Message"}
            </button>
          </form>
        </div>

        {/* Right: map */}
        <div className="border border-white/12 bg-[#131A2B] min-h-[500px] overflow-hidden">
          {c.map_embed_url ? (
            <iframe
              title="Google Maps"
              src={c.map_embed_url}
              width="100%" height="100%"
              className="min-h-[500px] w-full"
              loading="lazy" allowFullScreen referrerPolicy="no-referrer-when-downgrade"
              data-testid="contact-map"
            />
          ) : (
            <div className="flex items-center justify-center h-full text-white/40 min-h-[500px]">
              Map embed not configured yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
